/**
 * What Apply credit can pay, as one list (gdn-4321): visits that owe, and
 * charges on the account. Each carries its own cap, because the two go
 * through different verbs with different limits:
 *
 * - a visit takes up to `source.creditCents`, the visit verb's cap;
 * - a charge on the account takes up to `source.chargeCreditCents`, the
 *   account's unapplied credit, which is the charge verb's cap.
 *
 * Pure: the dialog renders these and posts the one the biller picks.
 */

import type {
  ChargeCreditAllocationRequest,
  ChargeCreditAllocationResponse,
  CreditAllocationRequest,
} from '../../store/v2-api';

import {
  type AccountChargeTarget,
  type CreditSource,
  type CreditTarget,
  formatCents,
} from './rows';

/** What the server applied. A charge also says whether it is now paid. */
export type AppliedCredit =
  | { target: 'visit'; request: CreditAllocationRequest }
  | {
      target: 'account_charge';
      request: ChargeCreditAllocationRequest;
      result: ChargeCreditAllocationResponse;
    };

export interface CreditOption {
  /** Unique across both kinds: a visit and a charge can share an id space. */
  key: string;
  kind: 'visit' | 'account_charge';
  /** The encounter id for a visit, the charge line id for a charge. */
  id: string;
  label: string;
  date: string | null;
  remainingDueCents: number;
  /** The most credit this option can take from the source. */
  capCents: number;
  /** Why it can't take credit yet; null when it can. */
  blockedReason: string | null;
}

const visitOption =
  (source: CreditSource) =>
  (target: CreditTarget): CreditOption => ({
    key: `visit:${target.encounterId}`,
    kind: 'visit',
    id: target.encounterId,
    label: target.label,
    date: target.date,
    remainingDueCents: target.remainingDueCents,
    capCents: source.creditCents,
    blockedReason: null,
  });

const chargeOption =
  (capCents: number) =>
  (charge: AccountChargeTarget): CreditOption => ({
    key: `charge:${charge.chargeLineId}`,
    kind: 'account_charge',
    id: charge.chargeLineId,
    label: charge.label,
    date: charge.date,
    remainingDueCents: charge.remainingDueCents,
    capCents,
    blockedReason: charge.blockedReason,
  });

/**
 * The options a source can pay, visits first. A kind the source can put no
 * money toward is left out entirely rather than listed as unpayable.
 */
export const creditOptionsOf = (
  source: CreditSource,
  visits: CreditTarget[],
  accountCharges: AccountChargeTarget[],
): CreditOption[] => {
  const chargeCapCents = source.chargeCreditCents ?? 0;
  return [
    ...(source.creditCents > 0 ? visits.map(visitOption(source)) : []),
    ...(chargeCapCents > 0 ? accountCharges.map(chargeOption(chargeCapCents)) : []),
  ];
};

/** What confirming applies: never more than the option's cap or its due. */
export const appliedCentsOf = (option: CreditOption): number =>
  Math.min(option.capCents, option.remainingDueCents);

/** The confirmation after credit lands, in the server's numbers. */
export const creditAppliedMessage = (applied: AppliedCredit): string => {
  if (applied.target === 'visit') {
    return `Applied ${formatCents(applied.request.amount_cents)} to the visit`;
  }
  const {
    applied_cents: appliedCents,
    charge_paid: paid,
    remaining_due_cents: left,
  } = applied.result;
  return paid
    ? `Applied ${formatCents(appliedCents)}. The charge is paid.`
    : `Applied ${formatCents(appliedCents)}. The charge still owes ${formatCents(left)}.`;
};
